import React, { useState } from "react";
import { useParams } from "react-router-dom";

import { useAppDispatch, useAppSelector } from "../../../app/store";

import SidepeekDetailsAccordion from "../../../components/ui/accordion/v2/SidepeekDetailsAccordion";
import EditableField from "../../../components/ui/formelement/v2/EditableField";

import {
  getAccountDetailByAccountNumber,
} from "../../creditAccounts/logic/slice";

import {
  UpdateCreditAccountDetailsRequest,
} from "../../../api/credit_account_details";

import {
  updateCreditAccountDetails,
} from "../logic/accountDetailsThunk";

import { showErrorToast } from "../../../lib/toast";

interface AccountExposureProps {
  accountNumber: string;
}

const AccountExposure: React.FC<AccountExposureProps> = ({
  accountNumber,
}) => {
  const { id: caseId } = useParams<{ id: string }>();

  const dispatch = useAppDispatch();

  const [editingField, setEditingField] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const accountDetails = useAppSelector((state) =>
    getAccountDetailByAccountNumber(state, accountNumber)
  );

  if (!accountDetails) {
    return null;
  }

  const creditAccountDetails = accountDetails.accountOverview?.credit_account_details;

  const exposure_credit_limit = creditAccountDetails?.exposure_credit_limit;

  const max_liability_exposure = creditAccountDetails?.max_liability_exposure;

  const handleSave = async (
    fieldKey: keyof UpdateCreditAccountDetailsRequest,
    value: string | number | null
  ): Promise<boolean> => {
    setIsSaving(true);
    try {
      const request: UpdateCreditAccountDetailsRequest = {
        [fieldKey]: value === null ? null : Number(value),
      };

      await dispatch(
        updateCreditAccountDetails({
          caseId: caseId!,
          accountNumber,
          request,
        })
      ).unwrap();

      setEditingField(null);
      return true;
    } catch (error) {
      showErrorToast("Failed to update exposure field. Please try again.");
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <SidepeekDetailsAccordion title="Exposure">
      <div className="flex w-full flex-col gap-2">
        <EditableField
          label="Credit Limit (in ₹)"
          value={exposure_credit_limit ?? null}
          type="currency"
          isEditable={true}
          isEditing={editingField === "exposure_credit_limit"}
          onEditStart={() => setEditingField("exposure_credit_limit")}
          onEditCancel={() => setEditingField(null)}
          onSave={(value) =>
            handleSave("exposure_credit_limit", value)
          }
          disabled={isSaving}
        />

        <EditableField
          label="Max Liability Exposure (in ₹)"
          value={max_liability_exposure ?? null}
          type="currency"
          isEditable={true}
          isEditing={editingField === "max_liability_exposure"}
          onEditStart={() => setEditingField("max_liability_exposure")}
          onEditCancel={() => setEditingField(null)}
          onSave={(value) =>
            handleSave("max_liability_exposure", value)
          }
          disabled={isSaving}
        />
      </div>
    </SidepeekDetailsAccordion>
  );
};

export default AccountExposure;